import React, { useEffect } from 'react';
import { Button, DatePicker, Drawer, Form, Input, Space, Spin } from 'antd';
import dayjs from 'dayjs';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseDobForPicker = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return dayjs(str.slice(0, 10), 'YYYY-MM-DD');
  }
  const parsed = dayjs(str);
  return parsed.isValid() ? parsed : null;
};

export default function BasicProfileEditDrawer({
  open,
  title = 'Edit Basic Profile',
  initialValues = {},
  loadingInitial = false,
  submitting = false,
  onClose,
  onSubmit,
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;

    const { mobile, email, dob } = initialValues || {};
    form.setFieldsValue({
      mobile: mobile || '',
      email: email || '',
      dob: parseDobForPicker(dob),
    });
  }, [open, initialValues, form]);

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const mobile = String(values.mobile || '').trim();
      const email = String(values.email || '').trim();
      const dob = values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : '';

      const payload = {
        mobile: mobile || null,
        email: email || null,
        dob: dob || null,
      };

      await onSubmit?.(payload);
    } catch (error) {
      if (error?.errorFields) return;
      throw error;
    }
  };

  return (
    <Drawer
      title={title}
      size={420}
      open={open}
      onClose={handleClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Save
          </Button>
        </Space>
      }
    >
      {loadingInitial ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="mobile"
            label="Mobile"
            rules={[
              {
                validator: (_, value) => {
                  const trimmed = String(value || '').trim();
                  if (!trimmed) return Promise.resolve();
                  if (!/^\d{10}$/.test(trimmed)) {
                    return Promise.reject(new Error('Enter a valid 10-digit mobile number'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Enter mobile number"
              maxLength={10}
              disabled={submitting}
              inputMode="numeric"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                validator: (_, value) => {
                  const trimmed = String(value || '').trim();
                  if (!trimmed) return Promise.resolve();
                  if (!EMAIL_REGEX.test(trimmed)) {
                    return Promise.reject(new Error('Enter a valid email address'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="email" placeholder="Enter email address" disabled={submitting} />
          </Form.Item>

          <Form.Item name="dob" label="Date of Birth">
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              placeholder="Select date of birth"
              disabled={submitting}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}
